import type { CostCategory, CostStatus, WorkOption } from 'used-vehicle-contracts';
import type { StatusTone } from '../../components/ui/StatusText';

/**
 * How a cost item is *shown* (plan §2.6).
 *
 * Colour discipline: work in progress is `warning` because somebody is spending money on it right now,
 * a waived item is `muted` because it costs nothing, and a finished one is `success` — its figure is the
 * only one on the row that is no longer a guess. An untouched estimate is left plain.
 */
export const COST_STATUS_TONES: Record<CostStatus, StatusTone> = {
  ESTIMATED: 'muted',
  IN_PROGRESS: 'warning',
  DONE: 'success',
  WAIVED: 'muted',
};

export const costStatusTone = (status: CostStatus): StatusTone => COST_STATUS_TONES[status] ?? 'muted';

/** The five buttons of the prototype's "current work estimates" toolbar, in its order (PP §7.3). */
export const WORK_OPTIONS: readonly Exclude<WorkOption, 'MANUAL'>[] = ['MINOR_BODY', 'MEDIUM_BODY', 'MAJOR_BODY', 'BATTERY', 'OIL_SERVICE'];

/** The statuses a person may pick; `WAIVED` is reached only through the administrator's action. */
export const EDITABLE_COST_STATUSES: readonly Exclude<CostStatus, 'WAIVED'>[] = ['ESTIMATED', 'IN_PROGRESS', 'DONE'];

/**
 * The categories a manual cost may be filed under. It is the contract's own list — which has **no
 * `HOLDING`**, because holding is produced by the `holding` equation alone and is therefore counted
 * exactly once (plan §3.3).
 */
export const COST_CATEGORIES: readonly CostCategory[] = [
  'RECONDITIONING',
  'LICENSING',
  'GENERAL_FEES',
  'REGISTRATION',
  'TRANSFER',
  'INSURANCE',
  'COMMISSION',
  'FUEL',
  'OVERHEAD',
  'TAX_TRAFFIC',
  'OTHER',
];
